import React, { forwardRef } from "react";
import { Pressable as RNPressable } from "react-native";
import type { WithLayoutPropsV2 } from "~/hoc/withLayoutPropsV2";
import { styleConstantsV2 as C } from "~/style/styleConstantsV2";
import type {
  PressableProps as RNPressableProps,
  View,
  ViewStyle,
} from "react-native";

export type PressableState = Readonly<{
  pressed: boolean;
  hovered?: boolean;
  focused?: boolean;
}>;

// We couldn't use withLayoutProps for Pressable component because it accepts style prop as a function
// and withLayoutProps is not designed to handle functions as props.
// To solve this, we created a custom Pressable component which can handle style prop as a function.

type NewPressableProps = Omit<RNPressableProps, keyof WithLayoutPropsV2> &
  WithLayoutPropsV2;

export const Pressable = forwardRef<View, NewPressableProps>(
  (
    {
      borderRadius8,
      borderRadius12,
      borderRadius20,
      borderRadius24,
      borderRadius30,

      padding4,
      padding8,
      padding16,
      padding24,
      padding32,
      padding40,
      padding48,
      padding56,
      padding64,
      padding72,
      padding80,
      padding88,
      padding96,

      paddingHorizontal4,
      paddingHorizontal8,
      paddingHorizontal16,
      paddingHorizontal24,
      paddingHorizontal32,
      paddingHorizontal40,
      paddingHorizontal48,
      paddingHorizontal56,
      paddingHorizontal64,
      paddingHorizontal72,
      paddingHorizontal80,
      paddingHorizontal88,
      paddingHorizontal96,

      paddingVertical4,
      paddingVertical8,
      paddingVertical16,
      paddingVertical24,
      paddingVertical32,
      paddingVertical40,
      paddingVertical48,
      paddingVertical56,
      paddingVertical64,
      paddingVertical72,
      paddingVertical80,
      paddingVertical88,
      paddingVertical96,

      centerContent,

      justifyContentCenter,
      justifyContentFlexStart,
      justifyContentFlexEnd,
      justifyContentSpaceBetween,
      justifyContentSpaceAround,
      justifyContentSpaceEvenly,

      alignItemsFlexStart,
      alignItemsFlexEnd,
      alignItemsCenter,
      alignItemsStretch,
      alignItemsBaseline,

      alignSelfFlexStart,
      alignSelfFlexEnd,
      alignSelfCenter,
      alignSelfStretch,
      alignSelfBaseline,

      flexDirectionRow,
      flexDirectionColumn,
      flexDirectionRowReverse,
      flexDirectionColumnReverse,

      absoluteTopLeft,
      absoluteTopLeftSmall,
      absoluteTopLeftMedium,
      absoluteTopLeftLarge,
      absoluteTopLeftExtraLarge,
      absoluteTopRight,
      absoluteTopRightSmall,
      absoluteTopRightMedium,
      absoluteTopRightLarge,
      absoluteTopRightExtraLarge,
      absoluteBottomLeft,
      absoluteBottomLeftSmall,
      absoluteBottomLeftMedium,
      absoluteBottomLeftLarge,
      absoluteBottomLeftExtraLarge,
      absoluteBottomRight,
      absoluteBottomRightSmall,
      absoluteBottomRightMedium,
      absoluteBottomRightLarge,
      absoluteBottomRightExtraLarge,

      colorPrimary,
      colorPrimaryHoverDark,
      colorPrimaryHoverLight,
      colorPrimaryMid,
      colorPrimaryBg,

      colorSecondary,
      colorSecondaryHoverLight,
      colorSecondaryHoverDark,
      colorSecondaryMid,
      colorSecondaryBg,

      colorTertiary,
      colorTertiaryHoverDark,
      colorTertiaryHoverLight,
      colorTertiaryMid,
      colorTertiaryBg,

      colorBlack,
      colorGrey,
      colorGrey50,
      colorGrey30,
      colorGrey20,
      colorGrey10,

      colorWhite,
      colorSuccess,
      colorSuccessBg,
      colorError,
      colorErrorBg,
      colorWarning,
      colorWarningBg,

      // see if we need these
      colorBackgroundWeb,
      colorLineBreak,

      colorBackgroundBackdrop,

      flex,
      flexWrap,
      aspectRatioOne,
      style: passThroughStyle,
      children,
      ...passThroughProps
    },
    ref,
  ) => {
    const style: ViewStyle = {};

    if (borderRadius8) style.borderRadius = C.borderRadius8;
    if (borderRadius12) style.borderRadius = C.borderRadius12;
    if (borderRadius20) style.borderRadius = C.borderRadius20;
    if (borderRadius24) style.borderRadius = C.borderRadius24;
    if (borderRadius30) style.borderRadius = C.borderRadius30;

    if (padding4) style.padding = C.spacing4;
    if (padding8) style.padding = C.spacing8;
    if (padding16) style.padding = C.spacing16;
    if (padding24) style.padding = C.spacing24;
    if (padding32) style.padding = C.spacing32;
    if (padding40) style.padding = C.spacing40;
    if (padding48) style.padding = C.spacing48;
    if (padding56) style.padding = C.spacing56;
    if (padding64) style.padding = C.spacing64;
    if (padding72) style.padding = C.spacing72;
    if (padding80) style.padding = C.spacing80;
    if (padding88) style.padding = C.spacing88;
    if (padding96) style.padding = C.spacing96;

    if (paddingHorizontal4) style.paddingHorizontal = C.spacing4;
    if (paddingHorizontal8) style.paddingHorizontal = C.spacing8;
    if (paddingHorizontal16) style.paddingHorizontal = C.spacing16;
    if (paddingHorizontal24) style.paddingHorizontal = C.spacing24;
    if (paddingHorizontal32) style.paddingHorizontal = C.spacing32;
    if (paddingHorizontal40) style.paddingHorizontal = C.spacing40;
    if (paddingHorizontal48) style.paddingHorizontal = C.spacing48;
    if (paddingHorizontal56) style.paddingHorizontal = C.spacing56;
    if (paddingHorizontal64) style.paddingHorizontal = C.spacing64;
    if (paddingHorizontal72) style.paddingHorizontal = C.spacing72;
    if (paddingHorizontal80) style.paddingHorizontal = C.spacing80;
    if (paddingHorizontal88) style.paddingHorizontal = C.spacing88;
    if (paddingHorizontal96) style.paddingHorizontal = C.spacing96;

    if (paddingVertical4) style.paddingVertical = C.spacing4;
    if (paddingVertical8) style.paddingVertical = C.spacing8;
    if (paddingVertical16) style.paddingVertical = C.spacing16;
    if (paddingVertical24) style.paddingVertical = C.spacing24;
    if (paddingVertical32) style.paddingVertical = C.spacing32;
    if (paddingVertical40) style.paddingVertical = C.spacing40;
    if (paddingVertical48) style.paddingVertical = C.spacing48;
    if (paddingVertical56) style.paddingVertical = C.spacing56;
    if (paddingVertical64) style.paddingVertical = C.spacing64;
    if (paddingVertical72) style.paddingVertical = C.spacing72;
    if (paddingVertical80) style.paddingVertical = C.spacing80;
    if (paddingVertical88) style.paddingVertical = C.spacing88;
    if (paddingVertical96) style.paddingVertical = C.spacing96;

    if (centerContent) {
      style.justifyContent = "center";
      style.alignItems = "center";
    }
    if (justifyContentCenter) style.justifyContent = "center";
    if (justifyContentFlexStart) style.justifyContent = "flex-start";
    if (justifyContentFlexEnd) style.justifyContent = "flex-end";
    if (justifyContentSpaceBetween) style.justifyContent = "space-between";
    if (justifyContentSpaceAround) style.justifyContent = "space-around";
    if (justifyContentSpaceEvenly) style.justifyContent = "space-evenly";

    if (alignItemsFlexStart) style.alignItems = "flex-start";
    if (alignItemsFlexEnd) style.alignItems = "flex-end";
    if (alignItemsCenter) style.alignItems = "center";
    if (alignItemsStretch) style.alignItems = "stretch";
    if (alignItemsBaseline) style.alignItems = "baseline";

    if (alignSelfFlexStart) style.alignSelf = "flex-start";
    if (alignSelfFlexEnd) style.alignSelf = "flex-end";
    if (alignSelfCenter) style.alignSelf = "center";
    if (alignSelfStretch) style.alignSelf = "stretch";
    if (alignSelfBaseline) style.alignSelf = "baseline";

    if (flexDirectionRow) style.flexDirection = "row";
    if (flexDirectionColumn) style.flexDirection = "column";
    if (flexDirectionRowReverse) style.flexDirection = "row-reverse";
    if (flexDirectionColumnReverse) style.flexDirection = "column-reverse";

    if (absoluteTopLeft) {
      style.position = "absolute";
      style.top = 0;
      style.left = 0;
    }
    if (absoluteTopLeftSmall) {
      style.position = "absolute";
      style.top = C.spacing4;
      style.left = C.spacing4;
    }
    if (absoluteTopLeftMedium) {
      style.position = "absolute";
      style.top = C.spacing8;
      style.left = C.spacing8;
    }
    if (absoluteTopLeftLarge) {
      style.position = "absolute";
      style.top = C.spacing16;
      style.left = C.spacing16;
    }
    if (absoluteTopLeftExtraLarge) {
      style.position = "absolute";
      style.top = C.spacing24;
      style.left = C.spacing24;
    }
    if (absoluteTopRight) {
      style.position = "absolute";
      style.top = 0;
      style.right = 0;
    }

    if (absoluteTopRightSmall) {
      style.position = "absolute";
      style.top = C.spacing4;
      style.right = C.spacing4;
    }

    if (absoluteTopRightMedium) {
      style.position = "absolute";
      style.top = C.spacing8;
      style.right = C.spacing8;
    }

    if (absoluteTopRightLarge) {
      style.position = "absolute";
      style.top = C.spacing16;
      style.right = C.spacing16;
    }

    if (absoluteTopRightExtraLarge) {
      style.position = "absolute";
      style.top = C.spacing24;
      style.right = C.spacing24;
    }

    if (absoluteBottomLeft) {
      style.position = "absolute";
      style.bottom = 0;
      style.left = 0;
    }

    if (absoluteBottomLeftSmall) {
      style.position = "absolute";
      style.bottom = C.spacing4;
      style.left = C.spacing4;
    }

    if (absoluteBottomLeftMedium) {
      style.position = "absolute";
      style.bottom = C.spacing8;
      style.left = C.spacing8;
    }

    if (absoluteBottomLeftLarge) {
      style.position = "absolute";
      style.bottom = C.spacing16;
      style.left = C.spacing16;
    }

    if (absoluteBottomLeftExtraLarge) {
      style.position = "absolute";
      style.bottom = C.spacing24;
      style.left = C.spacing24;
    }

    if (absoluteBottomRight) {
      style.position = "absolute";
      style.bottom = 0;
      style.right = 0;
    }

    if (absoluteBottomRightSmall) {
      style.position = "absolute";
      style.bottom = C.spacing4;
      style.right = C.spacing4;
    }

    if (absoluteBottomRightMedium) {
      style.position = "absolute";
      style.bottom = C.spacing8;
      style.right = C.spacing8;
    }

    if (absoluteBottomRightLarge) {
      style.position = "absolute";
      style.bottom = C.spacing16;
      style.right = C.spacing16;
    }

    if (absoluteBottomRightExtraLarge) {
      style.position = "absolute";
      style.bottom = C.spacing24;
      style.right = C.spacing24;
    }

    // primary
    if (colorPrimary) style.backgroundColor = C.colorPrimary;
    else if (colorPrimaryHoverLight)
      style.backgroundColor = C.colorPrimaryHoverLight;
    else if (colorPrimaryHoverDark)
      style.backgroundColor = C.colorPrimaryHoverDark;
    else if (colorPrimaryMid) style.backgroundColor = C.colorPrimaryMid;
    else if (colorPrimaryBg) style.backgroundColor = C.colorPrimaryBg;
    // secondary
    else if (colorSecondary) style.backgroundColor = C.colorSecondary;
    else if (colorSecondaryHoverLight)
      style.backgroundColor = C.colorSecondaryHoverLight;
    else if (colorSecondaryHoverDark)
      style.backgroundColor = C.colorSecondaryHoverDark;
    else if (colorSecondaryMid) style.backgroundColor = C.colorSecondaryMid;
    else if (colorSecondaryBg) style.backgroundColor = C.colorSecondaryBg;
    // tertiary
    else if (colorTertiary) style.backgroundColor = C.colorTertiary;
    else if (colorTertiaryHoverLight)
      style.backgroundColor = C.colorTertiaryHoverLight;
    else if (colorTertiaryHoverDark)
      style.backgroundColor = C.colorTertiaryHoverDark;
    else if (colorTertiaryMid) style.backgroundColor = C.colorTertiaryMid;
    else if (colorTertiaryBg) style.backgroundColor = C.colorTertiaryBg;
    // grey
    else if (colorBlack) style.backgroundColor = C.colorBlack;
    else if (colorGrey) style.backgroundColor = C.colorGrey;
    else if (colorGrey10) style.backgroundColor = C.colorGrey10;
    else if (colorGrey20) style.backgroundColor = C.colorGrey20;
    else if (colorGrey30) style.backgroundColor = C.colorGrey30;
    else if (colorGrey50) style.backgroundColor = C.colorGrey50;
    // white
    else if (colorWhite) style.backgroundColor = C.colorWhite;
    // success
    else if (colorSuccess) style.backgroundColor = C.colorSuccess;
    else if (colorSuccessBg) style.backgroundColor = C.colorSuccessBg;
    // error
    else if (colorError) style.backgroundColor = C.colorError;
    else if (colorErrorBg) style.backgroundColor = C.colorErrorBg;
    // warning
    else if (colorWarning) style.backgroundColor = C.colorWarning;
    else if (colorWarningBg) style.backgroundColor = C.colorWarningBg;
    // others
    else if (colorBackgroundBackdrop)
      style.backgroundColor = C.colorBackgroundBackdrop;
    // remove these below
    else if (colorBackgroundWeb) style.backgroundColor = C.colorBackgroundWeb;
    else if (colorLineBreak) style.backgroundColor = C.colorLineBreak;

    if (flex) style.flex = 1;

    if (flexWrap) style.flexWrap = "wrap";

    if (aspectRatioOne) {
      style.aspectRatio = 1;
    }

    return (
      <RNPressable
        ref={ref}
        style={(state: PressableState) => {
          return [
            style,
            typeof passThroughStyle === "function"
              ? passThroughStyle({
                  pressed: state.pressed,
                  hovered: state.hovered,
                } as any)
              : passThroughStyle,
          ];
        }}
        {...passThroughProps}
      >
        {children}
      </RNPressable>
    );
  },
);

export type Pressable = View;
export type PressableProps = RNPressableProps;
